import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getOperators, deleteOperator } from '../utils/storage';
import { CATEGORIES, TIERS, scoreUnit, getTier } from '../utils/scoring';

export default function Dashboard() {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getOperators().then(data => { setOperators(data); setLoading(false); });
  }, []);

  const handleDelete = async (id) => {
    if (!confirm('Delete this operator?')) return;
    await deleteOperator(id);
    setOperators(prev => prev.filter(o => o.id !== id));
  };

  // Aggregate stats
  const allUnits = operators.flatMap(o => (o.data?.units || []).map(u => ({ ...u, operatorName: o.data?.operatorName || o.operator_name })));
  const scored = allUnits.filter(u => +u.totalSales > 0).map(u => ({ ...u, results: scoreUnit(u) })).filter(u => u.results);

  // Tier distribution
  const tierDist = {};
  TIERS.forEach(t => { tierDist[t.name] = 0; });
  scored.forEach(u => u.results.forEach(r => { tierDist[r.tier.name]++; }));

  return (
    <div className="py-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-2xl text-white">Dashboard</h1>
          <p className="text-xs text-neutral-500">{operators.length} operator(s) · {allUnits.length} unit(s)</p>
        </div>
        <Link to="/calculator" className="btn-primary text-sm">+ New Operator</Link>
      </div>

      {/* Stats */}
      {scored.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
          {TIERS.map(tier => (
            <div key={tier.name} className="card p-4 text-center" style={{ borderTop: `3px solid ${tier.color}` }}>
              <p className="text-2xl font-black" style={{ color: tier.color }}>{tierDist[tier.name]}</p>
              <p className="text-[10px] text-neutral-500 uppercase tracking-wider">{tier.name}</p>
            </div>
          ))}
        </div>
      )}

      {/* Portfolio Overview */}
      {scored.length > 0 && (
        <div className="card p-5 mb-8">
          <h2 className="text-sm font-semibold text-brand-gold mb-4">Portfolio Tier Matrix</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-neutral-800">
                  <th className="text-left py-2 pr-3 text-neutral-500 font-medium">Operator</th>
                  <th className="text-left py-2 pr-3 text-neutral-500 font-medium">Unit</th>
                  <th className="text-left py-2 pr-3 text-neutral-500 font-medium">Market</th>
                  {CATEGORIES.map(c => <th key={c.id} className="text-center py-2 px-2 text-neutral-500 font-medium">{c.icon}</th>)}
                </tr>
              </thead>
              <tbody>
                {scored.slice(0, 20).map((u, i) => (
                  <tr key={i} className="border-b border-neutral-800/30">
                    <td className="py-2 pr-3 font-medium text-white">{u.operatorName}</td>
                    <td className="py-2 pr-3 text-neutral-400">{u.name}</td>
                    <td className="py-2 pr-3 text-neutral-500">{u.market}</td>
                    {u.results.map(r => (
                      <td key={r.id} className="text-center py-2 px-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-semibold ${r.tier.cls}`}>T{r.tier.num}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Operator List */}
      <div className="card p-5">
        <h2 className="text-sm font-semibold text-brand-gold mb-4">Saved Operators</h2>
        {loading ? (
          <p className="text-neutral-500 text-sm text-center py-4">Loading...</p>
        ) : operators.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-neutral-500 text-sm mb-4">No operators yet.</p>
            <Link to="/calculator" className="btn-primary text-sm">Create First Operator</Link>
          </div>
        ) : (
          <div className="space-y-2">
            {operators.map(op => {
              const units = op.data?.units || [];
              const unitCount = units.length;
              // Calculate portfolio score — average of all category final scores across all units
              const allResults = units.map(u => scoreUnit(u)).filter(Boolean).flat();
              const portfolioScore = allResults.length > 0 ? Math.round(allResults.reduce((s, r) => s + r.finalScore, 0) / allResults.length) : null;
              const portfolioTier = portfolioScore !== null ? getTier(portfolioScore) : null;
              return (
                <div key={op.id} className="flex items-center gap-4 p-3 rounded-lg hover:bg-neutral-800/30 transition-all">
                  {/* Portfolio score badge */}
                  {portfolioTier ? (
                    <div className="w-12 h-12 rounded-xl flex flex-col items-center justify-center flex-shrink-0" style={{ backgroundColor: portfolioTier.color + '15' }}>
                      <span className="text-lg font-black" style={{ color: portfolioTier.color }}>{portfolioScore}</span>
                      <span className="text-[7px]" style={{ color: portfolioTier.color }}>T{portfolioTier.num}</span>
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-neutral-800/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-neutral-700 text-xs">—</span>
                    </div>
                  )}
                  <div className="flex-1 cursor-pointer" onClick={() => navigate(`/operator/${op.id}`)}>
                    <p className="font-semibold text-white">{op.operator_name || 'Untitled'}</p>
                    <p className="text-[10px] text-neutral-500">
                      {unitCount} unit(s){portfolioTier ? ` · ${portfolioTier.name}` : ''} · Updated {new Date(op.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {units.slice(0, 3).map((u, i) => {
                      const results = scoreUnit(u);
                      if (!results) return null;
                      const topTier = results.reduce((best, r) => r.tier.num > best.tier.num ? r : best, results[0]);
                      return <span key={i} className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${topTier.tier.cls}`}>T{topTier.tier.num}</span>;
                    })}
                  </div>
                  <button onClick={() => handleDelete(op.id)} className="text-neutral-700 hover:text-red-400 text-xs">Delete</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="text-center text-[9px] text-neutral-700 mt-8">NEG Proprietary Category Support Scoring System</p>
    </div>
  );
}
